import React, { useEffect, useRef } from 'react';
import ModelSelector from './ModelSelector';
import ExpertSelector from './ExpertSelector';

const SendIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" /></svg>;

const PromptInput = ({
    value,
    onChange,
    onSubmit,
    onKeyDown,
    placeholder = "Message Omni...",
    isSubmitting = false,
    selectedModelConfigId,
    onModelSelect,
    selectedExpertId,
    onExpertSelect,
    isGhostMode = false,
    autoFocus = false,
    maxHeight = 180,
    className = "",
    containerClassName = ""
}) => {
    const textareaRef = useRef(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'inherit';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`;
        }
    }, [value, maxHeight]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
        }
        if (onKeyDown) onKeyDown(e);
    };

    return (
        <div className={`w-full relative ${containerClassName}`}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit();
                }}
                className={`relative flex flex-col bg-[var(--bg-tertiary)] border rounded-[24px] shadow-sm transition-all focus-within:bg-[var(--bg-tertiary)] ${isGhostMode
                    ? 'border-indigo-500/50 shadow-[0_0_20px_rgba(79,70,229,0.1)]'
                    : 'border-[var(--border-subtle)] focus-within:border-[var(--text-muted)]'
                    } ${className}`}
            >
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={isSubmitting}
                    autoFocus={autoFocus}
                    rows={1}
                    className="w-full bg-transparent text-[var(--text-main)] placeholder-[var(--text-muted)]/60 text-[15px] focus:outline-none resize-none px-6 py-4 min-h-[40px] custom-scrollbar overflow-y-auto font-medium rounded-t-[24px]"
                    style={{ maxHeight: `${maxHeight}px` }}
                />

                <div className="flex items-center justify-between px-5 pb-3 pt-0.5">
                    <div className="flex items-center gap-1">
                        <ModelSelector
                            selectedConfigId={selectedModelConfigId}
                            onSelect={onModelSelect}
                        />
                        <div className="w-[1px] h-4 bg-[var(--border-subtle)]/50 mx-0.5" />
                        <ExpertSelector
                            selectedExpertId={selectedExpertId}
                            onSelect={onExpertSelect}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={!value.trim() || isSubmitting}
                        className="shrink-0 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] active:scale-95 disabled:opacity-50 text-[var(--bg-main)] p-2.5 rounded-full transition-all flex items-center justify-center focus:outline-none shadow-md"
                    >
                        {isSubmitting ? (
                            <svg className="animate-spin w-4 h-4 text-inherit" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <SendIcon className="w-4 h-4 rotate-90" />
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PromptInput;
